import { Link, useNavigate } from "react-router-dom";
import { Flag, Gavel, Trophy, Users, ArrowRight, DollarSign, Calculator, BarChart3, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PBOLogo, SiteHeader } from "@/components/Logo";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getRecentSessions, removeRecentSession } from "@/lib/recentSessions";
import useAuth from "@/hooks/useAuth";
import useMyContests from "@/hooks/useMyContests";

// Shared, cached loader for the user's recent local sessions enriched with
// live state from the API (used by both LiveBanner and RecentContestsStrip).
let recentCache = { at: 0, promise: null };
const loadRecentItems = () => {
  if (recentCache.promise && Date.now() - recentCache.at < 30000) {
    return recentCache.promise;
  }
  recentCache = {
    at: Date.now(),
    promise: (async () => {
      const sessions = getRecentSessions(8);
      if (sessions.length === 0) return [];
      const results = await Promise.all(
        sessions.map(async (s) => {
          try {
            const { data } = await api.get(`/lookup/${s.code}`);
            let displayName = s.code;
            let status = null;
            let lockAt = null;
            try {
              if (data.kind === "contest") {
                const { data: c } = await api.get(`/contests/${s.code}`);
                displayName = c.name;
                status = c.status;
                lockAt = c.lock_at || null;
              } else {
                const { data: d } = await api.get(`/drafts/${s.code}`);
                displayName = d.draft?.name || d.name || s.code;
                status = d.draft?.status || d.status || null;
              }
            } catch {
              /* keep code as name */
            }
            return { ...s, kind: data.kind, displayName, status, lockAt };
          } catch {
            return { ...s, missing: true };
          }
        })
      );
      return results.filter((r) => !r.missing);
    })(),
  };
  return recentCache.promise;
};

const locksSoon = (it) => {
  if (it.status !== "open" || !it.lockAt) return false;
  const t = new Date(it.lockAt).getTime();
  const now = Date.now();
  return t > now && t - now < 24 * 3600 * 1000;
};

const lockTimeLabel = (lockAt) => {
  const d = new Date(lockAt);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return sameDay ? `today at ${time}` : `tomorrow at ${time}`;
};

export default function Landing() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [resolving, setResolving] = useState(false);
  const myContests = useMyContests();
  const hasContests = myContests.length > 0;

  const goJoin = async (e) => {
    e?.preventDefault();
    const c = code.trim().toUpperCase();
    if (c.length < 4) return;
    setResolving(true);
    try {
      const { data } = await api.get(`/lookup/${c}`);
      if (data.kind === "contest") {
        nav(`/contest/join/${c}`);
      } else {
        nav(`/join/${c}`);
      }
    } catch {
      toast.error("That code doesn't match any draft or contest");
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-pbo-cream pb-20 sm:pb-0">
      <SiteHeader
        rightSlot={
          <Link
            to="/join"
            className={
              hasContests
                ? "text-xs text-pbo-muted hover:text-pbo-green transition-colors"
                : "text-sm font-semibold text-pbo-green hover:text-pbo-gold transition-colors"
            }
            data-testid="header-join-link"
          >
            Have a code? Join →
          </Link>
        }
      />
      <LiveBanner />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pbo-dotted opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-pbo-cream pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-16 sm:pt-20 sm:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-in">
            <div className="flex items-center gap-2 mb-6">
              <span className="h-px w-10 bg-pbo-gold" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-pbo-gold">
                Est. 2017 · 10th Anniversary
              </span>
            </div>
            <h1 className="font-serif text-pbo-green text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              The draft room
              <br />
              <span className="font-script text-pbo-gold text-6xl sm:text-7xl lg:text-8xl block -mt-2">
                for your foursome
              </span>
              <span className="block">— two ways to play.</span>
            </h1>
            <p className="mt-6 text-pbo-ink/70 text-lg max-w-xl leading-relaxed">
              Run a live auction draft with your group, or build a salary-cap
              fantasy team. Pour a drink — it&apos;s tee time.
            </p>

            <RecentContestsStrip />

            <div className="mt-8">
              <form
                onSubmit={goJoin}
                className="flex items-center gap-2 bg-white border border-pbo-border rounded-full pl-5 pr-1 h-14 shadow-sm max-w-md"
              >
                <span className="text-xs uppercase tracking-[0.2em] text-pbo-muted font-semibold">
                  Code
                </span>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                  maxLength={6}
                  placeholder="ABCD"
                  className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-xl tracking-[0.3em] flex-1 text-pbo-green placeholder:text-pbo-border"
                  data-testid="landing-code-input"
                />
                <Button
                  type="submit"
                  disabled={resolving}
                  className="h-12 px-5 rounded-full bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft font-semibold"
                  data-testid="landing-join-btn"
                >
                  {resolving ? "..." : "Join"} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </form>
              <p className="text-xs text-pbo-muted mt-2 ml-2">
                Works for both auction drafts and salary-cap contests.
              </p>
            </div>
          </div>

          {/* Right – logo card */}
          <div className="lg:col-span-5 fade-in">
            <div className="relative">
              <div className="absolute -inset-8 pbo-stripe rounded-3xl opacity-70" />
              <div className="relative bg-white border border-pbo-border rounded-3xl p-10 shadow-[0_30px_60px_-30px_rgba(15,77,42,0.35)]">
                <div className="flex justify-center">
                  <PBOLogo size={260} />
                </div>
                <div className="mt-6 text-center">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold font-semibold">
                    Official Draft Room
                  </div>
                  <h3 className="font-serif text-pbo-green text-2xl mt-1">
                    Bid. Win. Wager.
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pick a mode */}
      <section className="bg-pbo-paper border-t border-pbo-border/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-pbo-gold">
                Pick your format
              </div>
              <h2 className="font-serif text-pbo-green text-3xl sm:text-4xl mt-2">
                What kind of draft are you running?
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <ModeCard
              testId="mode-auction-card"
              accent="green"
              icon={<Gavel className="w-6 h-6" />}
              tag="Live · Real-time"
              title="Live Auction Draft"
              tagline="Classic fantasy auction with live bidding."
              bullets={[
                "Set a budget per drafter & roster size",
                "Take turns nominating, everyone bids live",
                "Anti-snipe timer keeps it spicy",
              ]}
              cta="Host an auction"
              onClick={() => nav("/create")}
              ctaTestId="create-draft-cta"
            />
            <ModeCard
              testId="mode-contest-card"
              accent="gold"
              icon={<DollarSign className="w-6 h-6" />}
              tag="Async · Salary cap"
              title="Salary Cap Contest"
              tagline="DraftKings-style: pick 4 under a cap, points roll in live."
              bullets={[
                "Host sets a salary cap & assigns each golfer a price",
                "Friends quietly build the best team under the cap",
                "Live standings sync from a Google Sheet you control",
              ]}
              cta="Host a contest"
              onClick={() => nav("/contest/new")}
              ctaTestId="create-contest-cta"
            />
          </div>
        </div>
      </section>

      {/* How auction works */}
      <section className="bg-pbo-cream border-t border-pbo-border/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-pbo-gold">
                The auction format
              </div>
              <h2 className="font-serif text-pbo-green text-3xl sm:text-4xl mt-2">
                How the auction plays out
              </h2>
            </div>
            <p className="text-pbo-ink/60 max-w-md text-sm">
              Take turns nominating, everyone bids live, anti-snipe timer keeps
              things spicy.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <Step
              n="01"
              icon={<Flag className="w-5 h-5" />}
              title="Host sets the field"
              body="Enter your golfer list, budget per team, and roster size."
            />
            <Step
              n="02"
              icon={<Users className="w-5 h-5" />}
              title="Friends join with a code"
              body="A 4-letter room code is all they need. No accounts, no fuss."
            />
            <Step
              n="03"
              icon={<Gavel className="w-5 h-5" />}
              title="Nominate & bid live"
              body="Players take turns nominating golfers. Everyone else bids in real time."
            />
            <Step
              n="04"
              icon={<Trophy className="w-5 h-5" />}
              title="Build your team"
              body="Highest bidder wins. Repeat until every roster is full."
            />
          </div>
        </div>
      </section>

      {/* How contest works */}
      <section className="bg-pbo-paper border-t border-pbo-border/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-pbo-gold">
                The contest format
              </div>
              <h2 className="font-serif text-pbo-green text-3xl sm:text-4xl mt-2">
                How the salary cap plays out
              </h2>
            </div>
            <p className="text-pbo-ink/60 max-w-md text-sm">
              No live bidding. Pick your team privately before the deadline,
              then watch the standings update as scores roll in.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <Step
              n="01"
              icon={<Calculator className="w-5 h-5" />}
              title="Host prices the field"
              body="Add each golfer and assign a salary. Set the cap and pick deadline."
            />
            <Step
              n="02"
              icon={<Users className="w-5 h-5" />}
              title="Friends join with a code"
              body="Same simple flow — 4-letter code, display name, no accounts."
            />
            <Step
              n="03"
              icon={<DollarSign className="w-5 h-5" />}
              title="Build a team under cap"
              body="Pick four golfers, stay under the salary cap. Picks stay hidden until lock."
            />
            <Step
              n="04"
              icon={<BarChart3 className="w-5 h-5" />}
              title="Live standings"
              body="Scores sync from a Google Sheet you maintain. Anyone can check the leaderboard anytime."
            />
          </div>
        </div>
      </section>

      <footer className="py-8 text-center text-xs text-pbo-muted border-t border-pbo-border/60 space-x-4">
        <span>
          © {new Date().getFullYear()} The Palm Breezy Open Draft Room ·{" "}
          <span className="font-script text-pbo-gold text-base">cheers</span>
        </span>
        <Link
          to="/help"
          className="text-pbo-green hover:text-pbo-gold font-semibold"
          data-testid="footer-help-link"
        >
          Help · How to rejoin
        </Link>
      </footer>
      <MobileStickyBar />
    </div>
  );
}

const ModeCard = ({ testId, accent, icon, tag, title, tagline, bullets, cta, onClick, ctaTestId }) => {
  const isGold = accent === "gold";
  return (
    <div
      data-testid={testId}
      className={`group relative bg-white border rounded-2xl p-7 transition-all hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(15,77,42,0.4)] ${
        isGold ? "border-pbo-gold/40" : "border-pbo-border"
      }`}
    >
      <div className="flex items-center justify-between mb-5">
        <span
          className={`w-12 h-12 rounded-full grid place-items-center ${
            isGold ? "bg-pbo-gold/15 text-pbo-gold" : "bg-pbo-green/10 text-pbo-green"
          }`}
        >
          {icon}
        </span>
        <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pbo-muted">
          {tag}
        </span>
      </div>
      <h3 className="font-serif text-pbo-green text-2xl sm:text-3xl leading-tight">
        {title}
      </h3>
      <p className="text-pbo-ink/65 mt-2 text-sm">{tagline}</p>
      <ul className="mt-5 space-y-2 text-sm text-pbo-ink/75">
        {bullets.map((b) => (
          <li key={b} className="flex gap-2">
            <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${isGold ? "bg-pbo-gold" : "bg-pbo-green"}`} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onClick}
        data-testid={ctaTestId}
        className={`mt-7 w-full h-12 rounded-full font-semibold transition-transform active:scale-[0.98] ${
          isGold
            ? "bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft"
            : "bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep"
        }`}
      >
        {cta} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

const Step = ({ n, icon, title, body }) => (
  <div className="bg-white border border-pbo-border rounded-2xl p-6 hover:border-pbo-gold transition-colors">
    <div className="flex items-center justify-between mb-4">
      <span className="font-mono text-xs text-pbo-muted">{n}</span>
      <span className="w-9 h-9 rounded-full bg-pbo-green text-pbo-cream grid place-items-center">
        {icon}
      </span>
    </div>
    <h3 className="font-serif text-pbo-green text-xl">{title}</h3>
    <p className="text-sm text-pbo-ink/65 mt-2 leading-relaxed">{body}</p>
  </div>
);


// --- Mobile sticky footer: thumb-reach actions pinned to the bottom ---
const MobileStickyBar = () => {
  const nav = useNavigate();
  const contests = useMyContests();
  return (
    <div
      className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-pbo-cream/95 backdrop-blur border-t border-pbo-border px-4 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] flex gap-2.5"
      data-testid="mobile-sticky-bar"
    >
      <Button
        variant="outline"
        onClick={() => nav("/join")}
        className="flex-1 h-12 rounded-full border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream font-semibold"
        data-testid="sticky-join-btn"
      >
        Join with code
      </Button>
      <Button
        onClick={() => nav("/my-contests")}
        className="flex-1 h-12 rounded-full bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft font-semibold"
        data-testid="sticky-my-contests-btn"
      >
        <Trophy className="w-4 h-4 mr-1.5" />
        My contests
        {contests.length > 0 && (
          <span className="ml-1.5 bg-pbo-green text-pbo-cream rounded-full text-[10px] min-w-[18px] h-[18px] grid place-items-center px-1 font-bold">
            {contests.length}
          </span>
        )}
      </Button>
    </div>
  );
};

// --- Live-state banner: surfaces a live draft or an imminent lineup lock ---
const LiveBanner = () => {
  const nav = useNavigate();
  const contests = useMyContests();
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    let on = true;
    loadRecentItems().then((r) => on && setRecent(r));
    return () => {
      on = false;
    };
  }, []);

  const liveDraft = recent.find(
    (r) => r.kind === "draft" && r.status === "active"
  );
  const seen = new Set();
  const lockSoon = [
    ...recent.filter((r) => r.kind === "contest"),
    ...contests.map((c) => ({
      ...c,
      displayName: c.name || c.code,
      lockAt: c.lock_at,
    })),
  ].find((c) => {
    if (seen.has(c.code)) return false;
    seen.add(c.code);
    return locksSoon(c);
  });

  if (!liveDraft && !lockSoon) return null;
  const isLive = !!liveDraft;
  return (
    <button
      onClick={() =>
        nav(isLive ? `/draft/${liveDraft.code}` : `/contest/${lockSoon.code}`)
      }
      className="w-full bg-pbo-green text-pbo-cream text-sm font-semibold px-4 py-2.5 flex items-center justify-center gap-2 hover:bg-pbo-green-deep transition-colors"
      data-testid="live-banner"
    >
      <span
        className={`w-2 h-2 rounded-full shrink-0 ${
          isLive ? "bg-red-400 animate-pulse" : "bg-pbo-gold"
        }`}
      />
      <span className="truncate">
        {isLive
          ? `Your auction draft \u201c${liveDraft.displayName}\u201d is live`
          : `\u201c${lockSoon.displayName}\u201d locks ${lockTimeLabel(
              lockSoon.lockAt
            )}`}
      </span>
      <span className="underline underline-offset-2 shrink-0">
        {isLive ? "Rejoin →" : "Set your picks →"}
      </span>
    </button>
  );
};

// --- Recent contests/drafts strip ---
const RecentContestsStrip = () => {
  const nav = useNavigate();
  const { isSignedIn } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadRecentItems().then((results) => {
      if (cancelled) return;
      setItems(results);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onRemove = (e, code, kind) => {
    e.stopPropagation();
    removeRecentSession(code, kind);
    recentCache = { at: 0, promise: null };
    setItems((curr) => curr.filter((i) => !(i.code === code && i.kind === kind)));
  };

  if (loading && !isSignedIn) return null;
  if (items.length === 0 && !isSignedIn) return null;

  return (
    <div className="mt-8 max-w-md" data-testid="recent-contests-strip">
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pbo-muted">
          Jump back in
        </span>
        {isSignedIn && (
          <Link
            to="/my-contests"
            className="text-xs font-semibold text-pbo-gold hover:text-pbo-green transition-colors inline-flex items-center gap-1"
            data-testid="landing-my-contests-btn"
          >
            <Trophy className="w-3 h-3" />
            See all my contests →
          </Link>
        )}
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div
            key={`${it.kind}-${it.code}`}
            role="button"
            tabIndex={0}
            onClick={() =>
              nav(
                it.kind === "contest"
                  ? `/contest/${it.code}`
                  : `/draft/${it.code}`
              )
            }
            onKeyDown={(e) => {
              if (e.key === "Enter")
                nav(
                  it.kind === "contest"
                    ? `/contest/${it.code}`
                    : `/draft/${it.code}`
                );
            }}
            className="group w-full flex items-center gap-3 bg-white border border-pbo-border rounded-2xl pl-4 pr-2 py-3 hover:border-pbo-gold hover:shadow-[0_12px_28px_-18px_rgba(15,77,42,0.4)] transition-all cursor-pointer"
            data-testid={`recent-${it.kind}-${it.code}`}
          >
            <span
              className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
                it.kind === "contest"
                  ? "bg-pbo-gold/15 text-pbo-gold"
                  : "bg-pbo-green/10 text-pbo-green-soft"
              }`}
            >
              {it.kind === "contest" ? "Cap" : "Auction"}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-pbo-ink text-sm font-medium leading-snug">
                {it.displayName}
              </span>
              <span className="block font-mono text-[10px] tracking-[0.15em] text-pbo-muted mt-0.5">
                {it.code}
              </span>
            </span>
            {it.kind === "draft" && it.status === "active" && (
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                Live
              </span>
            )}
            {it.kind === "contest" && locksSoon(it) && (
              <span className="text-[9px] uppercase tracking-wider font-bold bg-pbo-gold/15 text-pbo-gold px-1.5 py-0.5 rounded-full shrink-0">
                Locks soon
              </span>
            )}
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => onRemove(e, it.code, it.kind)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onRemove(e, it.code, it.kind);
              }}
              className="p-1.5 rounded-full text-pbo-muted hover:text-red-600 hover:bg-pbo-paper transition-colors shrink-0"
              aria-label={`Remove ${it.code}`}
              data-testid={`remove-recent-${it.code}`}
            >
              <XIcon className="w-3 h-3" />
            </span>
            <ArrowRight className="w-4 h-4 text-pbo-muted group-hover:text-pbo-gold transition-colors shrink-0 mr-1" />
          </div>
        ))}
      </div>
    </div>
  );
};
